// Sentry initialisation for the web app. Imported once from main.tsx
// before React renders so anything that throws during boot is captured.
//
// We deliberately gate on `VITE_SENTRY_DSN` being non-empty so:
//   - dev builds run cleanly without a DSN
//   - tests don't accidentally emit telemetry
//   - PR-preview deploys can opt out by leaving the secret unset
//
// Trade-offs to be aware of as the app grows:
//   - tracesSampleRate is set deliberately low (10%) — cheap by default
//     but still gives meaningful perf visibility. Bump for an outage.
//   - Session Replay is enabled at 10% baseline, 100% on errors. The
//     replays mask form inputs (default) but otherwise capture text.
//     Revisit when we surface PII (incident descriptions, etc.).

import * as Sentry from '@sentry/react';

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    // The Vite plugin injects this at build time. Lets Sentry tie an
    // event back to the exact build that produced it.
    release: import.meta.env.VITE_SENTRY_RELEASE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Don't report errors that originate from extensions or third-party
    // injected scripts; they're rarely actionable.
    ignoreErrors: [
      /ResizeObserver loop limit exceeded/,
      /Non-Error promise rejection captured/,
    ],
  });
}

/**
 * Identify the current user to Sentry. Called by the AuthContext bridge
 * whenever the signed-in user changes. We send only uid + email — no
 * notification preferences, no profile-internal flags. Sentry's email
 * field is used to filter and group events, not for marketing.
 */
export function setSentryUser(input: { uid: string; email: string | null } | null): void {
  if (!import.meta.env.VITE_SENTRY_DSN) return;
  if (!input) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({
    id: input.uid,
    email: input.email ?? undefined,
  });
}

// Re-export the types and ErrorBoundary so callers can wrap in one import.
export { ErrorBoundary } from '@sentry/react';
